// create a context and provider to share state across MyCalendar components
import React, { createContext, useContext } from "react";

export const MyCalendarContext = createContext();

/**
 * Provider component to wrap around parts of the app that need access to MyCalendar context.
 * This helps avoid prop drilling by providing these values via context.
 * slotDropdown and setSlotDropdown are mainly used for managing the state of dropdowns in time slots across different calendar views.
 *
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - Nested components that will consume the context
 * @param {Function} props.setSlotDropdown - Function to update slot dropdown state (used to set new position, height and visibility)
 * @param {Object} props.slotDropdown - Current state of slot dropdown (used to track slot position, height and visibility). With help of slot position we can position the dropdown and event modal correctly for weekly view on master calendar and global header calendar
 * @param {React.RefObject} props.calendarStartDateRef - Ref to track the start date of the calendar view
 * @param {string} props.calendarType - Type of calendar view (can be: GLOBAL_HEADER_CALENDAR, MASTER_CALENDAR)
 * @returns {JSX.Element} The provider component
 */
const MyCalendarProvider = ({
  children,
  setSlotDropdown,
  slotDropdown,
  calendarStartDateRef,
  calendarType,
}) => {
  return (
    <MyCalendarContext.Provider
      value={{
        setSlotDropdown,
        slotDropdown,
        calendarStartDateRef,
        calendarType,
      }}
    >
      {children}
    </MyCalendarContext.Provider>
  );
};

export default MyCalendarProvider;

export const useMyCalendarContext = () => {
  const calendarContext = useContext(MyCalendarContext);
  if (!calendarContext) {
    throw new Error(
      "useMyCalendarContext must be used within a MyCalendarProvider"
    );
  }
  return calendarContext;
};
